import {
  BusinessAttributesFormType,
  BusinessDetailsFormType,
  CensusFormType,
  DescribeBusinessFormType,
  DocumentsFormType,
  EmployeeAttributesFormType,
  EPLIFormType,
} from "./formType";
import {
  BusinessDetailsType,
  BusinessDescriptionType,
  EPLIType,
  BusinessAttributeType,
  EmployeeAttributeType,
  DocumentType,
  SingleCensusType,
} from "types";
import {
  convertBoolIntoString,
  convertToTitleCase,
  ConvertToValidDate,
} from "utils/helper";

export const BusinessAttributesInitialFormValues = (
  formData: BusinessAttributeType
): BusinessAttributesFormType => {
  const hasBizActivities = formData?.companyInformation?.bizActivities || {};
  const documentList = formData?.applications?.[0]?.documents;
  return {
    hasPriorCarriers: convertBoolIntoString(hasBizActivities.hasPriorCarriers),
    priorCarriers: hasBizActivities.priorCarriers || "",
    hasHadWorkersCompPolicy: convertBoolIntoString(
      hasBizActivities.hasHadWorkersCompPolicy
    ),
    hasPastDuePremiums: convertBoolIntoString(
      hasBizActivities.hasPastDuePremiums
    ),
    beenCancelled: convertBoolIntoString(hasBizActivities.beenCancelled),
    policyCancelledReason: hasBizActivities.policyCancelledReason || "",
    beenDeclined: convertBoolIntoString(hasBizActivities.beenDeclined),
    sponsorSportsTeams: convertBoolIntoString(
      hasBizActivities.sponsorSportsTeams
    ),
    hasTaxLeinBankrupcy: convertBoolIntoString(
      hasBizActivities.hasTaxLeinBankrupcy
    ),
    offerHealthInsurance: convertBoolIntoString(
      hasBizActivities.offerHealthInsurance
    ),
    ownAircraftWatercraft: convertBoolIntoString(
      hasBizActivities.ownAircraftWatercraft
    ),
    use3rdPartyVendors: convertBoolIntoString(
      hasBizActivities.use3rdPartyVendors
    ),
    thirdPartyDetails1: hasBizActivities.thirdPartyDetails1 || "",
    thirdPartyDetails2: hasBizActivities.thirdPartyDetails2 || "",
    thirdPartyDetails3: hasBizActivities.thirdPartyDetails3 || "",
    thirdPartyDetails4: hasBizActivities.thirdPartyDetails4 || "",
    hasVolunteers: convertBoolIntoString(hasBizActivities.hasVolunteers),
    numberVolunteers: hasBizActivities.numberVolunteers?.toString() || "",
    hasVolunteerAccidentPolicy: convertBoolIntoString(
      hasBizActivities.hasVolunteerAccidentPolicy
    ),
    volunteerActivities: hasBizActivities.volunteerActivities || "",
    volunteerTravel: convertBoolIntoString(hasBizActivities.volunteerTravel),
    hazardousMats: convertBoolIntoString(hasBizActivities.hazardousMats),
    hasWrittenSafteyProg: convertBoolIntoString(
      hasBizActivities.hasWrittenSafteyProg
    ),
    materialSafetyDataSheets:
      documentList?.find(
        (item) => item.applicationField === "materialSafetyDataSheets"
      )?.uuid || "",
    volunteerAccidentPolicy:
      documentList?.find(
        (item) => item.applicationField === "volunteerAccidentPolicy"
      )?.uuid || "",
  };
};

export const BusinessDetailsInitialFormValues = (
  formData: BusinessDetailsType
): BusinessDetailsFormType => {
  const relatedEntity =
    formData?.relatedEntities && formData?.relatedEntities[0];

  return {
    companyName: formData?.companyName || "",
    dba: formData?.dba || "",
    ein: formData?.ein || "",
    entityType: formData?.companyInformation?.entityType || "",
    industry: formData?.companyInformation?.industry || "",
    industryNaics4: formData?.companyInformation?.industryNaics4 || "",
    industryNaics6: formData?.companyInformation?.industryNaics6 || "",
    industryOther: formData?.companyInformation?.industryOther || "",
    isNonProfit:
      convertBoolIntoString(formData?.companyInformation?.isNonProfit) || "",
    yearFounded: formData?.yearFounded?.toString() || "",
    hoursOfOperation: formData?.companyInformation?.hoursOfOperation || "",
    ownerHalfStakeOther:
      convertBoolIntoString(
        formData?.companyInformation?.ownerHalfStakeOther
      ) || "",
    employeesWorkOtherStakes:
      convertBoolIntoString(
        formData?.companyInformation?.employeesWorkOtherStakes
      ) || "",
    otherStakesDescription:
      formData?.companyInformation?.otherStakesDescription || "",
    relatedEntities: [
      {
        uuid: relatedEntity?.uuid || "",
        reName: relatedEntity?.legalName || "",
        reDescription: relatedEntity?.description || "",
        reIsDependent: convertBoolIntoString(relatedEntity?.isDependent) || "",
        reDba: relatedEntity?.dba || "",
        reEin: relatedEntity?.ein || "",
        // reDocuments: "",
        reHasW2Employees:
          convertBoolIntoString(relatedEntity?.hasW2Employees) || "",
        reHasOverlappingEmployees:
          convertBoolIntoString(relatedEntity?.hasOverlappingEmployees) || "",
        reSharesLocation:
          convertBoolIntoString(relatedEntity?.sharesLocation) || "",
      },
    ],
  };
};

export const DescribeBusinessInitialFormValues = (
  formData: BusinessDescriptionType
): DescribeBusinessFormType => {
  const documentList = formData?.applications?.[0]?.documents;
  return {
    businessDescription:
      formData?.companyInformation?.businessDescription || "",
    website: formData?.website?.length ? formData?.website : [""],
    verifyCompanyUrl:
      documentList?.find((item) => item.applicationField === "verifyCompanyUrl")
        ?.uuid || "",
    dailyOperationsOfEmployees:
      formData?.companyInformation?.dailyOperationsOfEmployees || "",
    hasUnionWorkers: convertBoolIntoString(
      formData?.companyInformation?.hasUnionWorkers
    ),
  };
};

export const DocumentsInitialFormValues = (
  formData: DocumentType
): DocumentsFormType => {
  const documentList = formData?.applications?.[0]?.documents;
  return {
    noLossRunsReportOtherReason:
      formData?.companyInformation?.noLossRunsReportOtherReason || "",
    noLossRunsReportReason:
      formData?.companyInformation?.noLossRunsReportReason || "",
    incorporationDocuments:
      documentList?.find(
        (item) => item.applicationField === "incorporationDocuments"
      )?.uuid || "",
    lossRunsReport:
      documentList?.find((item) => item.applicationField === "lossRunsReport")
        ?.uuid || "",
  };
};

export const EmployeeAttributesInitialFormValues = (
  formData: EmployeeAttributeType
): EmployeeAttributesFormType => {
  const hasEmployees = formData?.companyInformation?.hasEmployees || {};
  const hasBizActivities = formData?.companyInformation?.bizActivities || {};
  const hasTravelEmployees = hasEmployees?.employeesWhoTravel?.map(
    (travelData) => {
      return {
        employeeId: travelData.employeeId || "",
        travelPurpose: "",
        travelInternationally:
          convertBoolIntoString(travelData.travelInternationally) || "",
        travelLocation: travelData.travelLocation || "",
        travelFrequency: travelData.travelFrequency || "",
      };
    }
  );
  const hasEmployeesWhoUseTools = hasEmployees.employeesWhoUseTools?.map(
    (toolData) => {
      return {
        employeeId: toolData.employeeId || "",
        hardwareNames: toolData.hardwareNames || "",
      };
    }
  );
  const hasUnderageOrSeniorEmployees =
    hasEmployees.underageOrSeniorEmployees?.map((empData) => {
      return {
        dateOfBirth: ConvertToValidDate(empData.dateOfBirth) || "",
        employeeId: empData.employeeId || "",
        jobTitle: empData.jobTitle || "",
      };
    });
  const hasEmployeesWhoRequirePhysicalLabor =
    hasEmployees.employeesWhoRequirePhysicalLabor?.map((laborData) => {
      return {
        employeeId: laborData.employeeId || "",
        percentPhysicalLabor: laborData.percentPhysicalLabor || "",
      };
    });
  const hasEmployeesWhoUseBoatsBridges =
    hasEmployees.employeesWhoUseBoatsBridges || [""];
  const hasEmployeesWhoHavePhysicalDisabilities =
    hasEmployees.employeesWhoHavePhysicalDisabilities || [""];
  const hasEmployeesWhoWorkUnderground =
    hasEmployees.employeesWhoWorkUnderground?.map((data) => {
      return {
        employeeId: data.employeeId || "",
        undergroundDescription: data.undergroundDescription || "",
      };
    });
  const hasEmployeesWhoWorkOnLadders =
    hasEmployees.employeesWhoWorkOnLadders?.map((data) => {
      return {
        employeeId: data.employeeId || "",
        laddersDescription: data.laddersDescription || "",
      };
    });
  const hasEmployeesWhoPerformHeavyLifting =
    hasEmployees.employeesWhoPerformHeavyLifting?.map((data) => {
      return {
        employeeId: data.employeeId || "",
        maximumWeightLifted: data.maximumWeightLifted || "",
      };
    });
  const hasEmployeesWhoMakeDeliveries =
    hasBizActivities.employeesWhoMakeDeliveries?.map((data) => {
      return {
        employeeId: data.employeeId || "",
        deliveryTransportationType: data.deliveryTransportationType || "",
        deliveryDistance: data.deliveryDistance || "",
        deliveryFrequency: data.deliveryFrequency || "",
      };
    });

  return {
    travel: convertBoolIntoString(hasEmployees.travel),
    employeesWhoTravel: hasTravelEmployees || [
      {
        employeeId: "",
        travelFrequency: "",
        travelPurpose: "",
        travelInternationally: "",
        travelLocation: "",
      },
    ],
    employeesWhoUseTools: hasEmployeesWhoUseTools || [
      {
        employeeId: "",
        hardwareNames: "",
      },
    ],
    underageOrSeniorEmployees: hasUnderageOrSeniorEmployees || [
      {
        dateOfBirth: "",
        employeeId: "",
        jobTitle: "",
      },
    ],
    employeesWhoRequirePhysicalLabor: hasEmployeesWhoRequirePhysicalLabor || [
      {
        employeeId: "",
        percentPhysicalLabor: "",
      },
    ],
    employeesWhoUseBoatsBridges: hasEmployeesWhoUseBoatsBridges || [""],
    employeesWhoHavePhysicalDisabilities:
      hasEmployeesWhoHavePhysicalDisabilities || [""],
    hardware: convertBoolIntoString(hasEmployees.hardware),
    employeesPhysicalLabor: convertBoolIntoString(
      hasEmployees.employeesPhysicalLabor
    ),
    hasContractors: convertBoolIntoString(hasEmployees.hasContractors),
    employmentDuration: hasEmployees.employmentDuration || "",
    numberContractors: hasEmployees.numberContractors || "",
    percentContractors: hasEmployees.percentContractors || "",
    contractorWorkType: hasEmployees.contractorWorkType || "",
    contractorsHaveWorkComp: convertBoolIntoString(
      hasEmployees.contractorsHaveWorkComp
    ),
    contractorsHaveWorkersCompCois: convertBoolIntoString(
      hasEmployees.contractorsHaveWorkersCompCois
    ),
    numberTemp: hasEmployees.numberTemp || "",
    boatsBridges: convertBoolIntoString(hasEmployees.boatsBridges),
    physicalDisability: convertBoolIntoString(hasEmployees.physicalDisability),
    employeesWorkUnderground: convertBoolIntoString(
      hasEmployees.employeesWorkUnderground
    ),
    employeesWorkOnLadders: convertBoolIntoString(
      hasEmployees.employeesWorkOnLadders
    ),
    employeesWhoWorkUnderground: hasEmployeesWhoWorkUnderground || [
      {
        employeeId: "",
        undergroundDescription: "",
      },
    ],
    employeesWhoWorkOnLadders: hasEmployeesWhoWorkOnLadders || [
      {
        employeeId: "",
        laddersDescription: "",
      },
    ],
    deliveries: convertBoolIntoString(hasBizActivities.deliveries),
    employeesWhoMakeDeliveries: hasEmployeesWhoMakeDeliveries || [
      {
        employeeId: "",
        deliveryTransportationType: "",
        deliveryDistance: "",
        deliveryFrequency: "",
      },
    ],
    underageOrSenior: convertBoolIntoString(hasEmployees.underageOrSenior),
    employeePerformHeavyLifting: convertBoolIntoString(
      hasEmployees.employeePerformHeavyLifting
    ),
    employeesWhoPerformHeavyLifting: hasEmployeesWhoPerformHeavyLifting || [
      {
        employeeId: "",
        maximumWeightLifted: "",
      },
    ],
  };
};

export const EPLIInitialFormValues = (formData: EPLIType): EPLIFormType => {
  const hasPolicy = formData?.companyInformation?.epli || {};
  const documentList = formData?.applications?.[0]?.documents;
  return {
    hasPolicy: convertBoolIntoString(hasPolicy.hasPolicy),
    epliDocument:
      documentList?.find((item) => item.applicationField === "epliDocument")
        ?.uuid || "",
    epliLossDocument:
      documentList?.find((item) => item.applicationField === "epliLossDocument")
        ?.uuid || "",
    receivedEpliAllegations: convertBoolIntoString(
      hasPolicy.receivedEpliAllegations
    ),
    beenSuedFormerEmp: convertBoolIntoString(hasPolicy.beenSuedFormerEmp),
    claimDetails: hasPolicy.claimDetails || "",
    overTwentyFiveEe: convertBoolIntoString(hasPolicy.overTwentyFiveEe),
    offeredSeverancePackages: convertBoolIntoString(
      hasPolicy.offeredSeverancePackages
    ),
    noEpliLossRunsReportReason: hasPolicy.noEpliLossRunsReportReason || "",
    noEpliLossRunsReportOtherReason:
      hasPolicy.noEpliLossRunsReportOtherReason || "",
  };
};

export const CensusInitialFormValues = (
  formData?: SingleCensusType
): CensusFormType => {
  const employeeInformation = formData?.employeeInformation;
  return {
    censusType: formData?.isCompanyOwner
      ? "Owner"
      : formData?.isCompanyEmployee
      ? "Employee"
      : "",
    firstName: formData?.firstName || "",
    middleName: formData?.middleName || "",
    lastName: formData?.lastName || "",
    title: employeeInformation?.title || "",
    roleDescription: employeeInformation?.roleDescription || "",
    isPaid: employeeInformation?.isPaid || "",
    salaryType: employeeInformation?.salaryType || "",
    salary: employeeInformation?.salary?.toString() || "",
    hourlyRate: employeeInformation?.hourlyRate?.toFixed(2) || "",
    hoursWeekly: employeeInformation?.hoursWeekly?.toString() || "",
    ownerStake: employeeInformation?.ownerStake?.toString() ?? "",
    uuid: formData?.uuid || "",
    email: formData?.email || "",
    isRemote: convertBoolIntoString(employeeInformation?.isRemote),
    address1: employeeInformation?.remoteAddress?.address1 || "",
    address2: employeeInformation?.remoteAddress?.address2 || "",
    city: employeeInformation?.remoteAddress?.city || "",
    state: employeeInformation?.remoteAddress?.state || "",
    zip: employeeInformation?.remoteAddress?.zip || "",
    autoValidated: employeeInformation?.remoteAddress?.autoValidated,
    officeLocationId: formData?.officeLocationId || "",
    exemptionStatus:
      convertToTitleCase(employeeInformation?.exemptionStatus) || "",
    employeeType: formData?.employeeType || "",
    adminAccess: convertBoolIntoString(employeeInformation?.adminAccess),
  };
};
